import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, GripVertical, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { MasterExportButton } from '@/components/efs/MasterExportButton';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { useFacilityOptions } from '@/features/settings/users/useUsers';

import { WarehousesFormDialog } from './WarehousesFormDialog';
import { WarehousesRowActions } from './WarehousesRowActions';
import { useReorderWarehouses, useWarehousesList } from './useWarehouses';
import type { Warehouse } from './types';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

export const WarehousesPage = () => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const query = useWarehousesList({ active_only: false, page, per_page: perPage });
  const rows = useMemo(() => query.data?.rows ?? [], [query.data?.rows]);
  const facilitiesQuery = useFacilityOptions();
  const facilityNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of facilitiesQuery.data ?? []) map.set(f.facility_id, f.facility_name);
    return map;
  }, [facilitiesQuery.data]);
  const total = query.data?.meta?.total ?? 0;
  const { mutate: reorderWarehouses, isPending: isReordering } = useReorderWarehouses();

  const canManage = hasPermission(user, 'master.approve');
  const canReorderCurrentPage = canManage && page === 1;

  const changePerPage = useCallback((nextPerPage: number) => {
    setPerPage(nextPerPage);
    setPage(1);
  }, []);

  const reorderWarehouseRows = useCallback(
    (nextRows: Warehouse[]) => {
      reorderWarehouses(nextRows.map((row) => row.warehouse_id));
    },
    [reorderWarehouses],
  );

  const moveWarehouse = useCallback(
    (id: string, direction: -1 | 1) => {
      const index = rows.findIndex((row) => row.warehouse_id === id);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= rows.length) return;
      const nextRows = [...rows];
      [nextRows[index], nextRows[nextIndex]] = [nextRows[nextIndex], nextRows[index]];
      reorderWarehouseRows(nextRows);
    },
    [reorderWarehouseRows, rows],
  );

  const dropWarehouse = (targetId: string) => {
    if (!draggingId || draggingId === targetId) return;
    const from = rows.findIndex((row) => row.warehouse_id === draggingId);
    const to = rows.findIndex((row) => row.warehouse_id === targetId);
    if (from < 0 || to < 0) return;
    const nextRows = [...rows];
    const [moved] = nextRows.splice(from, 1);
    nextRows.splice(to, 0, moved);
    reorderWarehouseRows(nextRows);
  };

  const columns = useMemo<MasterTableColumn<Warehouse>[]>(
    () => [
      {
        key: 'code',
        header: t('efs.masters.warehouses.fields.code', { ns: 'masters' }),
        cell: (row) => <span className="font-mono text-xs">{row.code}</span>,
      },
      {
        key: 'display_name',
        header: t('efs.masters.warehouses.fields.display_name', { ns: 'masters' }),
        cell: (row) => row.display_name,
      },
      {
        key: 'facility_id',
        header: t('efs.masters.warehouses.fields.facility_id', { ns: 'masters' }),
        // Show the site name, not its UUID (the raw ID meant nothing to
        // operators — 2026-07-30 field report).
        cell: (row) =>
          row.facility_id ? (facilityNames.get(row.facility_id) ?? row.facility_id) : '—',
      },
      {
        key: 'sort_order',
        header: t('efs.masters.warehouses.fields.sort_order', { ns: 'masters' }),
        align: 'center',
        cell: (row) => {
          if (!canManage) return row.sort_order ?? '—';
          const index = rows.findIndex((item) => item.warehouse_id === row.warehouse_id);
          return (
            <div className="flex justify-center gap-1">
              <span
                className="inline-flex h-8 w-8 cursor-grab items-center justify-center text-efs-text-muted"
                title={t('efs.masters.actions.drag_to_reorder', { ns: 'masters' })}
              >
                <GripVertical className="h-4 w-4" />
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={!canReorderCurrentPage || isReordering || index <= 0}
                aria-label={t('efs.masters.actions.move_up', { ns: 'masters' })}
                title={t('efs.masters.actions.move_up', { ns: 'masters' })}
                onClick={() => moveWarehouse(row.warehouse_id, -1)}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={
                  !canReorderCurrentPage || isReordering || index < 0 || index >= rows.length - 1
                }
                aria-label={t('efs.masters.actions.move_down', { ns: 'masters' })}
                title={t('efs.masters.actions.move_down', { ns: 'masters' })}
                onClick={() => moveWarehouse(row.warehouse_id, 1)}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
      {
        key: 'active',
        header: t('efs.masters.warehouses.fields.active', { ns: 'masters' }),
        cell: (row) =>
          row.active_flag ? (
            <Badge variant="default">
              {t('efs.common.status.approved', { defaultValue: '有効' })}
            </Badge>
          ) : (
            <Badge variant="outline">
              {t('efs.common.status.retired', { defaultValue: '無効' })}
            </Badge>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => <WarehousesRowActions row={row} />,
      },
    ],
    [canManage, canReorderCurrentPage, facilityNames, isReordering, moveWarehouse, rows, t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.masters.warehouses.title', { ns: 'masters' })}
        subtitle={t('efs.masters.warehouses.subtitle', { ns: 'masters' })}
        count={total}
        actions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('efs.masters.actions.create', { ns: 'masters' })}
            </Button>
          ) : null
        }
      />

      <TableToolbar
        count={t('efs.masters.toolbar.count', { ns: 'masters', count: total })}
        actions={<MasterExportButton kind="warehouses" params={{ active_only: false }} />}
      />

      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.warehouse_id}
        rowProps={(row) =>
          canReorderCurrentPage
            ? {
                draggable: !isReordering,
                onDragStart: () => setDraggingId(row.warehouse_id),
                onDragOver: (event) => event.preventDefault(),
                onDrop: (event) => {
                  event.preventDefault();
                  dropWarehouse(row.warehouse_id);
                  setDraggingId(null);
                },
                onDragEnd: () => setDraggingId(null),
                className:
                  draggingId === row.warehouse_id ? 'cursor-grabbing opacity-60' : 'cursor-grab',
              }
            : undefined
        }
        isLoading={query.isLoading}
        pagination={{
          page,
          perPage,
          total,
          onPageChange: setPage,
          onPerPageChange: changePerPage,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />

      <WarehousesFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

export default WarehousesPage;
